// The one shell every funnel screen renders inside.
//
// It owns the three shared elements — the answer ribbon (progress + back
// navigation), the left rail with the vertical's process flow, and the forward
// action — and it reads all three from the journey config, so a screen
// supplies only its body. This replaces the twelve hand-rolled copies of the
// same layout (and the Motor-only `_MotorShell`) with a single configurable
// component.
//
// Layout modes, chosen per step via `layout` in the config:
//   "form" (default) — rail on the left, content on the right
//   "wide"           — full-width content, no rail (plan lists, compare, summary)
//
// Forward navigation, chosen per journey or per step via `navigation`:
//   placement "inline" — the action is a bar pinned to the bottom of the
//                        viewport, above the scrolling content. On a step with
//                        a rail it spans only the content column, so the rail
//                        stays readable underneath instead of being covered.
//   placement "ribbon" — a compact action lives in the sticky ribbon only
//   placement "both"   — (default) the pinned bar; the ribbon copy is
//                        redundant while it is on screen, so it stays hidden
//
// The shell is a flex column the height of the viewport: <main> takes what the
// header and ribbon leave, so a step whose form fits does not scroll at all.
//
// The pinned bar sits outside <main> and its height is measured, not assumed,
// so <main> can reserve exactly that much padding. That keeps the bar in one
// place regardless of how tall the step's content is, while still guaranteeing
// the last field scrolls clear of it.
import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AppBarComponent from "../elements/AppBarComponent";
import AnswerTrail from "../navigation/AnswerTrail";
import JourneyRail from "../navigation/JourneyRail";
import FlowCta from "../navigation/FlowCta";
import { useAppBarSticky } from "../../hooks/useAppBarSticky";
import { NAVBAR_SPACER_SX } from "../../utils/layout";
import { findStepByPath, navigationFor, railFor, stepAt } from "../../config/journeys";
import { markStepReached } from "../../redux/actions";

const PAGE_BG = "linear-gradient(180deg, #fdf6ea 0%, #FBF1E2 30%, #fff 100%)";

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]  Step body. Falls back to <Outlet/> so
 *        this doubles as a react-router layout route.
 * @param {boolean} [props.loading]           Blocking overlay while saving/fetching.
 * @param {Object} [props.cta]                { label, onClick, disabled, busy, progress,
 *        nextIncomplete, secondary }. Omit to render no forward action (the step
 *        config's `hideCta` does the same).
 * @param {number} [props.maxWidth]
 * @param {boolean} [props.compactRail]       Let the rail shed its bullet list on
 *                                            short screens (see JourneyRail).
 */
const JourneyShell = ({ children, loading, cta, maxWidth = 1280, compactRail = false }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((/** @type {import("../../redux/types").RootState} */ state) => !!state.agent.currentAgentId);
  const insuranceType = useSelector((/** @type {import("../../redux/types").RootState} */ state) => state.insuranceType);
  const { isAppBarFixed, isXsScreen } = useAppBarSticky();

  const match = findStepByPath(location.pathname, insuranceType);

  // Record that this step was reached, which is what makes the earlier pills
  // in the stepper clickable. Done here rather than in each page so no screen
  // can forget to and strand the user without back navigation.
  useEffect(() => {
    if (match) dispatch(markStepReached(match.journey.key, match.index));
  }, [match?.journey.key, match?.index, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whether the inline forward action is currently on screen. While it is,
  // the ribbon's compact copy stays hidden — there is never more than one
  // Continue visible at a time.
  const [footerInView, setFooterInView] = useState(true);
  const onFooterInView = useCallback((visible) => setFooterInView(visible), []);

  // How much room the pinned bar needs at the bottom of the content. Starts at
  // 0 and is set by the bar's own measurement on mount; screens with no
  // forward action never set it and get no wasted space.
  const [ctaHeight, setCtaHeight] = useState(0);
  const onCtaHeight = useCallback((height) => setCtaHeight(height), []);

  const step = match?.step;
  const rail = match ? railFor(match.journey, step) : null;
  const navigation = navigationFor(match?.journey, step);
  const isWide = step?.layout === "wide" || !rail;
  const body = children ?? <Outlet />;
  const showCta = Boolean(cta && !step?.hideCta);
  const inline = showCta && navigation.placement !== "ribbon";
  const ribbon = showCta && navigation.placement !== "inline";
  const next = match ? stepAt(match.journey.key, match.index + 1) : null;

  const footer = inline ? (
    <FlowCta
      {...cta}
      pinned
      maxWidth={maxWidth}
      // A wide step has no rail, so nothing to leave room for.
      railOffset={!isWide}
      next={navigation.nextHint === false ? undefined : next?.label}
      onHeightChange={onCtaHeight}
      onInViewChange={onFooterInView}
    />
  ) : null;

  const ribbonAction = ribbon
    ? {
        label: cta.label || "Continue",
        onClick: cta.onClick,
        busy: cta.busy,
        disabled: cta.disabled,
        progress: cta.progress,
        nextIncomplete: cta.nextIncomplete,
      }
    : null;

  return (
    // A column exactly as tall as the viewport, not taller. The shell used to
    // be a bare fragment whose children simply stacked: the navbar spacer, the
    // ribbon, and a <main> with `minHeight: 60vh` — a floor that ignored
    // everything above it — plus a bottom reserve ADDED on top of that floor.
    // The three together cleared the viewport on their own, so every form
    // scrolled a little whether or not it had anything to scroll to.
    //
    // Now <main> takes the space the header and ribbon leave and no more, and
    // its reserve sits inside that (box-sizing: border-box, set globally in
    // index.css). A form that fits does not scroll; one that does not grows
    // normally.
    <Box
      sx={{
        minHeight: "100vh",
        // Mobile browsers count the collapsing address bar in 100vh, which
        // leaves a sliver of scroll on a page that otherwise fits exactly.
        "@supports (min-height: 100dvh)": { minHeight: "100dvh" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppBarComponent
        isAppBarFixed={isAppBarFixed}
        isLoggedIn={isLoggedIn}
        isXsScreen={isXsScreen}
      />
      <Box sx={NAVBAR_SPACER_SX} />
      <AnswerTrail
        action={ribbonAction}
        // With no inline footer the ribbon is the only action, so it is always
        // shown; otherwise it stands in only while the footer is off screen.
        showAction={Boolean(ribbonAction) && (!inline || !footerInView)}
      />

      {loading && (
        <Box
          role="status"
          aria-live="polite"
          aria-label="Loading"
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress sx={{ color: "#C9A777" }} />
        </Box>
      )}

      {/* The page scrolls normally, underneath the pinned bar. The bar's own
          measured height is added to the usual bottom padding so the last
          field always clears it — including on a phone, where the bar is
          taller because its contents stack. */}
      <Box
        component="main"
        sx={{
          background: PAGE_BG,
          // Takes the leftover height, never shrinks below its content.
          flex: "1 0 auto",
          // Clearance for the pinned bar. The measured height already includes
          // the bar's own float margin, so this is breathing room between the
          // last field and the bar — not a second copy of the bar's height.
          pb: `calc(${ctaHeight}px + 24px)`,
        }}
      >
        <Box sx={{ maxWidth, mx: "auto", px: { xs: 3, md: 6 }, pt: { xs: 2, md: 4 } }}>
          {isWide ? (
            body
          ) : (
            <Grid2 container spacing={{ xs: 3, md: 4 }}>
              <Grid2 size={{ tablet: 4 }} sx={{ display: { xs: "none", tablet: "block" } }}>
                <JourneyRail rail={rail} compact={compactRail} />
              </Grid2>

              {/* Mobile keeps the heading but drops the rail — the process-flow
                  image and reassurance bullets don't survive a 390px column. */}
              <Grid2 size={{ xs: 12 }} sx={{ display: { xs: "block", tablet: "none" }, mb: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#3d322b",
                    fontWeight: 700,
                    fontSize: "1.6rem",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  {step?.mobileTitle || `${rail.title || ""} ${rail.accent || ""}`.trim()}
                </Typography>
                {rail.subtitle && (
                  <Typography sx={{ color: "#7B6E66", mt: 1, fontSize: "0.9375rem", lineHeight: 1.6 }}>
                    {rail.subtitle}
                  </Typography>
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, tablet: 8 }}>
                {/* A plain block wrapper, not the Grid2 item directly: MUI v6
                    Grid2 items carry flex-container styles, which a step body
                    laying itself out in normal flow should not inherit. */}
                <Box sx={{ width: "100%" }}>{body}</Box>
              </Grid2>
            </Grid2>
          )}
        </Box>
      </Box>

      {footer}
    </Box>
  );
};

export default JourneyShell;
